import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  APP_MODE,
  Appointment,
  BasicAppointmentSettings,
  BusinessNotification,
  BusinessObject,
  CachedCalendarData,
  CalendarAdditionalConfig,
  Customer,
  DATE_FORMAT,
  Staff,
} from '../../../models';
import { CalendarAppointmentHistory } from '../model/CalendarAppointmentHistory.interface';
import { AbstractStorageService } from '../../../services';
import { CustomerUtils, DateTimeUtils } from '../../../utils';

@Injectable({
  providedIn: 'root',
})
export class CalendarStorageService {
  private readonly MAX_CACHED_APPOINTMENT_LIFE_TIME_IN_DAYS = 30;
  private readonly MAX_NUMBER_OF_HISTORY = 50;
  private appointmentHistory: CalendarAppointmentHistory[] = [];

  private calendarMode$$: BehaviorSubject<APP_MODE>;
  public readonly calendarMode$: Observable<APP_MODE>;

  private calendarAdditionalConfig$$: BehaviorSubject<CalendarAdditionalConfig>;
  public readonly calendarAdditionalConfig$: Observable<CalendarAdditionalConfig>;

  private recentlyCreatedAppointment$$: BehaviorSubject<Appointment>;
  public readonly recentlyCreatedAppointment$: Observable<Appointment>;

  private recentlyUpdatedAppointment$$: BehaviorSubject<Appointment>;
  public readonly recentlyUpdatedAppointment$: Observable<Appointment>;

  // These stored customers are used in case the calendar cannot load the customer data (due to internet connection)
  // but the customer data is loaded later on by the appointment dialog.
  // It will help to avoid loading customer data multiple times.
  private customers$$: BehaviorSubject<Customer[]>;
  public readonly customers$: Observable<Customer[]>;

  private scrollToDateTime$$: BehaviorSubject<Date>;
  public readonly scrollToDateTime$: Observable<Date>;

  private businessNotifications$$: BehaviorSubject<BusinessNotification[]>;
  public readonly businessNotifications$: Observable<BusinessNotification[]>;

  constructor(private storageService: AbstractStorageService) {
    this.calendarMode$$ = new BehaviorSubject<APP_MODE>(APP_MODE.ADMIN);
    this.calendarMode$ = this.calendarMode$$.asObservable();

    this.calendarAdditionalConfig$$ =
      new BehaviorSubject<CalendarAdditionalConfig>(null);
    this.calendarAdditionalConfig$ =
      this.calendarAdditionalConfig$$.asObservable();

    this.recentlyCreatedAppointment$$ = new BehaviorSubject<Appointment>(null);
    this.recentlyCreatedAppointment$ =
      this.recentlyCreatedAppointment$$.asObservable();

    this.recentlyUpdatedAppointment$$ = new BehaviorSubject<Appointment>(null);
    this.recentlyUpdatedAppointment$ =
      this.recentlyUpdatedAppointment$$.asObservable();

    this.customers$$ = new BehaviorSubject<Customer[]>([]);
    this.customers$ = this.customers$$.asObservable();

    this.scrollToDateTime$$ = new BehaviorSubject<Date>(null);
    this.scrollToDateTime$ = this.scrollToDateTime$$.asObservable();

    this.businessNotifications$$ = new BehaviorSubject<BusinessNotification[]>(
      []
    );
    this.businessNotifications$ = this.businessNotifications$$.asObservable();
  }

  public setCalendarMode(calendarMode: APP_MODE): void {
    if (calendarMode !== this.getCalendarMode()) {
      this.calendarMode$$.next(calendarMode || APP_MODE.ADMIN);
    }
  }

  public getCalendarMode(): APP_MODE {
    return this.calendarMode$$.value;
  }

  public setCalendarAdditionalConfig(config: CalendarAdditionalConfig): void {
    this.calendarAdditionalConfig$$.next(config);
  }

  public getCalendarAdditionalConfig(): CalendarAdditionalConfig {
    return this.calendarAdditionalConfig$$.value;
  }

  public setRecentlyCreatedAppointment(appointment: Appointment): void {
    if (appointment?.id) {
      this.recentlyCreatedAppointment$$.next(appointment);
    }
  }

  public setRecentlyUpdatedAppointment(appointment: Appointment): void {
    if (appointment?.id) {
      this.recentlyUpdatedAppointment$$.next(appointment);
    }
  }

  public setCustomers(customers: Customer[]): void {
    if (customers?.length > 0) {
      this.customers$$.next(customers);
    }
  }

  public setScrollToDateTime(dateTime: Date): void {
    this.scrollToDateTime$$.next(dateTime);
  }

  public saveAppointmentToHistory(appointment: Appointment): void {
    const matchedIndexInHistory = this.appointmentHistory?.findIndex(
      (a) => a.appointment.id === appointment.id
    );
    if (matchedIndexInHistory < 0) {
      this.appointmentHistory.push({
        appointment,
        lastSeen: new Date(),
      });
    } else {
      this.appointmentHistory[matchedIndexInHistory] = {
        appointment,
        lastSeen: new Date(),
      };
    }

    this.appointmentHistory.sort(
      (a, b) => b.lastSeen.getTime() - a.lastSeen.getTime()
    );

    this.appointmentHistory = this.appointmentHistory.slice(
      0,
      this.MAX_NUMBER_OF_HISTORY
    );
  }

  public getAppointmentHistory(): CalendarAppointmentHistory[] {
    return this.appointmentHistory;
  }

  public removeBlockerFromHistory(blockerId: number): void {
    this.appointmentHistory = this.appointmentHistory?.filter(
      (a) => a.appointment.id !== blockerId
    );
  }

  public removeSeriesBlockerFromHistory(seriesId: string): void {
    this.appointmentHistory = this.appointmentHistory?.filter(
      (a) =>
        !a.appointment.serieBlockerId ||
        a.appointment.serieBlockerId !== seriesId
    );
  }

  public setBusinessNotifications(
    businessNotifications: BusinessNotification[]
  ): void {
    this.businessNotifications$$.next(businessNotifications);
  }

  // region Cached calendar data
  public async getCachedBusinessObjectById(
    businessObjectId: number
  ): Promise<BusinessObject> {
    const cachedMapData = await this.getCachedCalendarData();
    return cachedMapData.get(businessObjectId)?.businessObject;
  }

  public async saveCachedBusinessObject(businessObject: BusinessObject) {
    const cachedMapData = await this.getCachedCalendarData();

    const currentData =
      cachedMapData.get(businessObject?.id) || this.generateDefaultCachedData();
    cachedMapData.set(businessObject?.id, { ...currentData, businessObject });

    await this.cleanupAndSaveCachedCalendarData(cachedMapData);
  }

  public async getCachedAppointmentSettings(
    businessObjectId: number
  ): Promise<BasicAppointmentSettings> {
    const cachedMapData = await this.getCachedCalendarData();
    return cachedMapData.get(businessObjectId)?.appointmentSettings;
  }

  public async saveCachedAppointmentSettings(
    businessObjectId: number,
    appointmentSettings: BasicAppointmentSettings
  ) {
    const cachedMapData = await this.getCachedCalendarData();

    const currentData =
      cachedMapData.get(businessObjectId) || this.generateDefaultCachedData();
    cachedMapData.set(businessObjectId, {
      ...currentData,
      appointmentSettings,
    });

    await this.cleanupAndSaveCachedCalendarData(cachedMapData);
  }

  public async getCachedStaffs(businessObjectId: number): Promise<Staff[]> {
    const cachedMapData = await this.getCachedCalendarData();
    return cachedMapData.get(businessObjectId)?.staffs;
  }

  public async saveCachedStaffs(businessObjectId: number, staffs: Staff[]) {
    const cachedMapData = await this.getCachedCalendarData();

    const currentData =
      cachedMapData.get(businessObjectId) || this.generateDefaultCachedData();
    cachedMapData.set(businessObjectId, { ...currentData, staffs });

    await this.cleanupAndSaveCachedCalendarData(cachedMapData);
  }

  public async saveCachedAppointments(appointments: Appointment[]) {
    const businessObjectId: number = appointments?.[0]?.locationId;
    if (!businessObjectId) {
      return;
    }

    const cachedMapData = await this.getCachedCalendarData();

    const currentData =
      cachedMapData.get(businessObjectId) || this.generateDefaultCachedData();
    appointments?.forEach((appointment) =>
      currentData.mapOfIdAndAppointments?.set(appointment.id, appointment)
    );

    cachedMapData.set(businessObjectId, currentData);

    await this.cleanupAndSaveCachedCalendarData(cachedMapData);
  }

  public async getCachedAppointmentById(
    appointmentId: number
  ): Promise<Appointment> {
    const cachedMapData = await this.getCachedCalendarData();

    for (const cachedData of cachedMapData.values()) {
      const appointment =
        cachedData?.mapOfIdAndAppointments?.get(appointmentId);
      if (appointment) {
        return appointment;
      }
    }
    return undefined;
  }

  public async getCachedAppointmentByRange(
    businessObjectId: number,
    fromDate: Date,
    toDate: Date
  ): Promise<Appointment[]> {
    const cachedMapData = await this.getCachedCalendarData();
    const currentData =
      cachedMapData.get(businessObjectId) || this.generateDefaultCachedData();

    const fromDateAsString: string = DateTimeUtils.formatDateToString(
      fromDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toDateAsString: string = DateTimeUtils.formatDateToString(
      toDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    const result: Appointment[] = [];

    if (!currentData?.mapOfIdAndAppointments?.size) {
      return result;
    }

    for (const appointment of currentData.mapOfIdAndAppointments.values()) {
      const appointmentDate = DateTimeUtils.formatDateToString(
        appointment?.startTimeAsDate,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      );
      if (
        appointmentDate >= fromDateAsString &&
        appointmentDate <= toDateAsString
      ) {
        result.push(appointment);
      }
    }

    return result;
  }

  private async getCachedCalendarData(): Promise<
    Map<number, CachedCalendarData>
  > {
    try {
      const cachedMapData = await this.storageService.getCachedCalendarData();

      if (!cachedMapData?.size) {
        return new Map<number, CachedCalendarData>();
      }

      const cachedMapDataToReturn = new Map<number, CachedCalendarData>();

      cachedMapData.forEach((cachedData, boId) => {
        if (cachedData.mapOfIdAndAppointments?.size) {
          for (const appointment of cachedData.mapOfIdAndAppointments.values()) {
            cachedData.mapOfIdAndAppointments.set(
              appointment.id,
              CustomerUtils.transformAppointmentForFrontend(appointment)
            );
          }
        }

        cachedMapDataToReturn.set(boId, cachedData);
      });

      return cachedMapDataToReturn;
    } catch (e) {
      console.error(e);
      return new Map<number, CachedCalendarData>();
    }
  }

  private async cleanupAndSaveCachedCalendarData(
    cachedMapData: Map<number, CachedCalendarData>
  ) {
    try {
      const lastDayForCaching = DateTimeUtils.getBeginOfDate(
        DateTimeUtils.addDayToDate(
          new Date(),
          -this.MAX_CACHED_APPOINTMENT_LIFE_TIME_IN_DAYS
        )
      );

      const cachedMapDataToSave = new Map<number, CachedCalendarData>();

      cachedMapData.forEach((cachedData, boId) => {
        if (cachedData.mapOfIdAndAppointments?.size) {
          for (const appointment of cachedData.mapOfIdAndAppointments.values()) {
            if (appointment.startTimeAsDate < lastDayForCaching) {
              cachedData.mapOfIdAndAppointments.delete(appointment.id);
            }
          }
        }

        cachedMapDataToSave.set(boId, cachedData);
      });

      await this.storageService.saveCachedCalendarData(cachedMapDataToSave);
    } catch (e) {
      console.error(e);
    }
  }

  private generateDefaultCachedData(): CachedCalendarData {
    return {
      mapOfIdAndAppointments: new Map<number, Appointment>(),
    };
  }
  // endregion
}
